// @flow
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

export const PLUS_VERSION_NOTES_SETTING = 'finect-plus-version-notes';

export const useVersionNotes = () => {
  const setting = useSelector(state => state.settings.settings[PLUS_VERSION_NOTES_SETTING]);
  const { value, fetched, fetching } = setting || {};

  const data = useMemo(() => {
    if (!value) return [];
    return Object.keys(value)
      .filter(key => !isNaN(Number(key)))
      .map(key => value[key])
      .filter(v => v && v.version && String(v.version).trim() !== '' && String(v.version) !== 'null');
  }, [value]);

  return useMemo(
    () => ({
      data,
      fetched: Boolean(fetched),
      fetching: Boolean(fetching),
      latestVersion: data.length > 0 ? data[0] : null
    }),
    [data, fetched, fetching]
  );
};

export const parseVersionItem = (item: any) => {
  const isObject = typeof item === 'object' && item !== null;
  return {
    text: isObject ? item.text ? String(item.text) : '' : String(item || ''),
    important: isObject ? Boolean(item.important) : false
  };
};

export const isFinecter = (user: ?Object): boolean => {
  if (!user) return false;

  // Check by organization ID
  const hasFinectOrg = user.organizations?.some(org => org.id === 'D98F19ECF61B11E8B22202000AEE01C9');
  if (hasFinectOrg) return true;

  // Check by email domain
  if (typeof user.email === 'string' && user.email.toLowerCase().endsWith('@finect.com')) return true;

  return false;
};

export const renderText = (text: string, color?: string = '#29cb82') => {
  if (!text) return '';
  const parts = String(text).split(/(\[.*?\]\(.*?\))/g);
  return parts.map((part, i) => {
    const match = part.match(/\[(.*?)\]\((.*?)\)/);
    if (match) {
      const isExternal = match[2].startsWith('http');
      if (isExternal) {
        return (
          <a
            key={i}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color, fontWeight: 600, textDecoration: 'underline' }}
          >
            {match[1]}
          </a>
        );
      }
      return (
        <Link key={i} to={match[2]} style={{ color, fontWeight: 600, textDecoration: 'underline' }}>
          {match[1]}
        </Link>
      );
    }
    return part;
  });
};

/**
 * Días que una versión sigue siendo "novedad" a efectos del modal de aviso.
 *
 * El "visto" solo vive en el localStorage de este navegador, así que quien vuelve tras meses,
 * estrena dispositivo o se acaba de suscribir recibiría como novedad algo publicado hace mucho.
 * Pasado el plazo la versión deja de anunciarse sola: sigue en /plus/novedades, que es donde vive
 * el changelog completo.
 */
export const VERSION_NOTES_MAX_AGE_DAYS = 30;

const LAST_SEEN_KEY = 'finect_plus_last_version';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const MONTHS = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  setiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12
};

// Mediodía y no medianoche: evita que el desfase horario mueva la fecha un día, que en una ventana
// de 30 días no cambia nada pero haría el cálculo dependiente del huso. Un mes fuera de rango es
// fecha mal escrita, no una fecha rara: se rechaza en vez de dejar que JavaScript la desborde al
// año siguiente.
const toDate = (year, month, day) => (month >= 1 && month <= 12 ? new Date(year, month - 1, day, 12) : null);

const FORMATS = [
  // ISO, con o sin hora detrás: 2026-09-15
  [/^(\d{4})-(\d{1,2})-(\d{1,2})/, ([, year, month, day]) => toDate(year, month, day)],
  // Español con día primero: 15/09/2026, 15-09-2026
  [/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/, ([, day, month, year]) => toDate(year, month, day)],
  // Mes en letra, que es lo que escribe el editor del backoffice. Cubre tanto lo que autogenera al
  // crear una versión ('15 de septiembre de 2026') como lo que sugiere su placeholder, que separa
  // el año con coma ('3 de Marzo, 2026'). El día es opcional: 'Septiembre 2026' se ancla al día 1.
  [
    /^(?:(\d{1,2})\s+de\s+)?([a-záéíóú]+)[\s,]+(?:de\s+)?(\d{4})$/,
    ([, day, name, year]) => toDate(year, MONTHS[name], day || 1)
  ]
];

/**
 * Interpreta la fecha de una nota de versión, que en el ajuste del backoffice es texto libre.
 *
 * Devuelve null si no reconoce el formato: aquí no se inventa una fecha, porque una fecha inventada
 * silencia o resucita un aviso sin que se note.
 */
export const parseVersionDate = (date: any): ?Date => {
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;

  const text = String(date || '')
    .trim()
    .toLowerCase();

  return FORMATS.reduce((found, [pattern, build]) => {
    if (found) return found;
    const match = text.match(pattern);

    return match ? build(match) : null;
  }, null);
};

/**
 * ¿Esta nota sigue siendo lo bastante reciente como para anunciarla?
 *
 * Sin fecha reconocible devuelve true, que es el comportamiento de siempre: más vale un aviso de
 * más que callar una novedad real porque alguien escribió la fecha de otra forma.
 */
export const isVersionRecent = (note: ?Object): boolean => {
  const date = parseVersionDate(note?.date);

  return !date || (Date.now() - date.getTime()) / MS_PER_DAY <= VERSION_NOTES_MAX_AGE_DAYS;
};

// El almacenamiento puede lanzar (Safari restringido, navegación privada) y esto corre dentro de un
// layout effect, donde una excepción se lleva por delante el render de toda la página Plus. Sin
// memoria de lo visto se enseña el aviso, que es el mal menor.
const readLastSeenVersion = (): ?string => {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch (error) {
    return null;
  }
};

export const rememberSeenVersion = (version: any) => {
  try {
    localStorage.setItem(LAST_SEEN_KEY, version);
  } catch (error) {
    // Si no se puede recordar, el aviso reaparecerá; no es motivo para dejar el modal abierto.
  }
};

/**
 * ¿Hay que anunciar esta versión en el modal? Reciente y todavía no vista en este navegador.
 */
export const shouldAnnounceVersion = (note: ?Object): boolean =>
  Boolean(note) && isVersionRecent(note) && readLastSeenVersion() !== String(note.version);
